/* eslint-disable max-lines -- this file should be split */
import { A, G } from '@mobily/ts-belt';
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { isAfter } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BooleanInput,
  required,
  SelectInput,
  UserIdentity,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { useFormContext, useFormState } from 'react-hook-form';
import { useQuery } from 'react-query';

import {
  generatePlanTypeLabeled,
  getCampaignProcessingOptionsFromType,
  isCashSourceSupportedByMatchingContributionConfig,
} from '@Common/bo/b2b_campaigns';
import { isExemptFromCsgCrdsCampaign } from '@Common/bo/campaign_personal_assets';
import { FINANCIAL_INDEX_SHORT_NAME } from '@Common/constants/financial_indexes';
import { esContractTypes } from '@Common/types/b2b/contracts';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import {
  B2bCampaignType,
  B2bRelatedCampaignCorrectionReasons,
  GetB2bCampaignsIdEndpoint,
  GetB2bCampaignsResponse,
} from '@Common/types/bo/b2b/campaigns';
import { GetB2bCompaniesIdEndpoint } from '@Common/types/bo/b2b/companies';
import { B2bCampaignActionHandler, GetB2bContractsResponse } from '@Common/types/bo/b2b/contracts';
import { GetPlansMatchingContributionConfigByYearResponse } from '@Common/types/bo/b2b/matching_contribution_config';
import { GetB2bCompaniesPlansResponse } from '@Common/types/bo/b2b/plans';
import {
  areContractsAgreementDistributionsSimilar,
  getFirstContractIndex,
  mutualizeCashSources,
} from '@Common/validators/contracts';

import {
  contractAllocationChoicesFor,
  contractCampaignActionHandlerChoices,
} from '@sh4res/bo-common/choices/contracts';
import { ShowSection } from '@sh4res/bo-common/components';
import {
  b2bCampaignReasonChoices,
  b2bCampaignTypeChoices,
  valueSharingBonusFiscalRuleChoices,
} from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { isCampaignVacationDaysAllocation } from '@sh4res/bo-common/components/B2bCampaigns/helpers/isCampaignVacationDaysAllocation';
import {
  isCompanyVoluntaryDepositCashSourceInput,
  isEmployeeVoluntaryDepositCashSourceInput,
  MatchingContributionRelatedCashSourceInput,
  splitVoluntaryDepositCashSourceChoices,
  toMatchingContributionRelatedCashSource,
  toMatchingContributionRelatedCashSourceInput,
} from '@sh4res/bo-common/components/B2bCampaigns/helpers/matchingContributionRelatedCashSource';
import { OperationAmountSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/GeneralDetailsStep/components/OperationAmountSection';
import { ReferenceFiscalPeriod } from '@sh4res/bo-common/components/B2bCampaigns/steps/GeneralDetailsStep/components/ReferenceFiscalPeriod';
import { VacationDaysSetupSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/GeneralDetailsStep/components/VacationDaysSetupSection';
import { B2bCompanySelectInput, B2bContractArraySelectInput } from '@sh4res/bo-common/fields';
import { ProductsField } from '@sh4res/bo-common/fields/ProductsField';
import {
  useHistoricalFinancialIndexValue,
  useResourceBasePath,
  useValueChanged,
  useWatchObject,
} from '@sh4res/bo-common/hooks';
import { formatCentsNumber, inputDisabledProps } from '@sh4res/bo-common/utils';
import { isBoExternalIdentity } from '@sh4res/bo-common/utils/b2b_auth_role';
import { tryToLocalDate } from '@sh4res/bo-common/utils/date';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type Company = GetB2bCompaniesIdEndpoint['Client']['ResponseBody'];
type Params = {
  identity: UserIdentity | undefined;
  isCompanyVoluntaryDepositsDisplayed: boolean;
  isMatchingContributionTypeDisplayed: boolean;
  contractCashSources?: unknown[] | null;
};
const getTypeChoices = ({
  isCompanyVoluntaryDepositsDisplayed,
  isMatchingContributionTypeDisplayed,
  identity,
  contractCashSources,
}: Params) =>
  isBoExternal() && identity?.roles.includes('admin_level_2')
    ? b2bCampaignTypeChoices.filter((choice) => {
        if (!isCompanyVoluntaryDepositsDisplayed) {
          return false;
        }
        return choice.id === 'voluntary_deposit';
      })
    : b2bCampaignTypeChoices.filter((choice) => {
        if (['ccb_matured_election', 'withdrawal_at_maturity'].includes(choice.id)) {
          return false;
        }

        if (choice.id === 'matching_contribution' && isMatchingContributionTypeDisplayed) {
          return true;
        }

        if (!isCompanyVoluntaryDepositsDisplayed && choice.id === 'voluntary_deposit') {
          return false;
        }
        if (contractCashSources && !contractCashSources.includes(choice.id)) {
          return false;
        }
        return true;
      });

const getMatchingContributionRelatedCashSourceChoices = (
  contract?: GetB2bContractsResponse[number] | null,
  plans?: GetB2bCompaniesPlansResponse | null,
) => {
  if (G.isNullable(contract) || G.isNullable(plans)) {
    return [];
  }

  const allPlansCashSources = plans.reduce(
    (acc: B2bCashSource[], plan) => [...new Set(acc.concat(plan.cash_sources))],
    [],
  );
  const cashSourcesAllowedInPlan = Object.keys(contract.campaigns).filter((key) =>
    A.includes(allPlansCashSources, key),
  );
  return splitVoluntaryDepositCashSourceChoices(
    b2bCampaignTypeChoices.filter((type) => A.includes(cashSourcesAllowedInPlan, type.id)),
  );
};

const hasPlanCashSourceWithMatchingContribution = (
  plan: GetB2bCompaniesPlansResponse[number],
  campaignType: string,
) => {
  const cashSource = plan.cash_sources.find((planCashSource) => planCashSource === campaignType);
  return (
    G.isNotNullable(cashSource) &&
    isCashSourceSupportedByMatchingContributionConfig(plan.matching_contribution_config, cashSource)
  );
};

const shouldRederiveFromContract = ({
  isOnBoExternal,
  isProcessingOptionsDirty,
}: {
  isOnBoExternal: boolean;
  isProcessingOptionsDirty: boolean;
}) => isOnBoExternal || !isProcessingOptionsDirty;

// allocation_computed_by is locked once derived as 'company' (it can't go back to 'distributor'),
// so a stale 'distributor' picked from a previous contract must be replaced even when dirty.
const shouldLockAllocationToCompany = (
  isCreate: boolean,
  derivedAllocationComputedBy: B2bCampaignActionHandler | null | undefined,
) => isCreate && derivedAllocationComputedBy === 'company';

// eslint-disable-next-line max-lines-per-function -- makes sense to be grouped
export const GeneralDetailsStep = ({
  action,
  isOngoingConsultationEdit = false,
  isCetConsultationPeriodEdit = false,
}: {
  action: 'create' | 'edit';
  isOngoingConsultationEdit?: boolean;
  isCetConsultationPeriodEdit?: boolean;
  // eslint-disable-next-line sonarjs/cognitive-complexity -- makes sense to be grouped
}) => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const testId = 'BoExternalB2bCampaignCreateGeneralDetailsStep';
  const record = useRecordContext<GetB2bCampaignsIdEndpoint['Client']['ResponseBody']>();
  const resourceBasePath = useResourceBasePath();
  const { setValue, getValues } = useFormContext();
  const { dirtyFields } = useFormState();
  const isProcessingOptionsDirty = Boolean(
    dirtyFields.allocation_computed_by || dirtyFields.choice_collected_by || dirtyFields.payout_by,
  );

  const [isMatchingContributionManuallyOverridden, setIsMatchingContributionManuallyOverridden] =
    useState(false);
  const isOnBoExternal = isBoExternal();

  // enabled on creation or on draft
  const editionDisabled = useMemo(() => !!record && record.status !== 'campaign_draft', [record]);
  const isStartDateInFuture = useMemo(
    () => !!record?.start_date && isAfter(record.start_date, new Date()),
    [record?.start_date],
  );

  // Processing options may still be edited once the campaign is published, as long as it hasn't
  // actually started yet; once it has, changing them would leave already-collected choices
  // computed under a different rule.
  const isCampaignAlreadyStarted = action === 'edit' && editionDisabled && !isStartDateInFuture;

  const {
    company_id: companyId,
    amount_type_input: amountTypeInput,
    b2b_contract_ids: contractIds,
    type,
    allocation_computed_by: allocationComputedBy,
    matching_contribution_computed_by: mcComputedBy,
    contract_cash_sources: contractCashSources,
    value_sharing_bonus_fiscal_rule: valueSharingBonusFiscalRule,
    choice_collected_by: choiceCollectedBy,
    matching_contribution_related_cash_source: matchingContributionRelatedCashSource,
    matching_contribution_related_cash_source_input: matchingContributionRelatedCashSourceInput,
    reason,
    reference_year: referenceYear,
  } = useWatchObject<{
    company_id?: number | null;
    b2b_contract_ids?: number[] | null;
    type?: B2bCampaignType | null;
    amount_type_input?: 'net' | 'gross' | null;
    reason?: string | null;
    reference_year?: number | null;
    allocation_computed_by?: B2bCampaignActionHandler | null;
    matching_contribution_computed_by?: B2bCampaignActionHandler | null;
    choice_collected_by?: B2bCampaignActionHandler | null;
    value_sharing_bonus_fiscal_rule?: string | null;
    matching_contribution_related_cash_source?: B2bCashSource | null;
    matching_contribution_related_cash_source_input?: MatchingContributionRelatedCashSourceInput | null;
    contract_cash_sources?: B2bCashSource[] | null;
  }>()([
    'company_id',
    'amount_type_input',
    'b2b_contract_ids',
    'type',
    'allocation_computed_by',
    'matching_contribution_computed_by',
    'contract_cash_sources',
    'value_sharing_bonus_fiscal_rule',
    'choice_collected_by',
    'matching_contribution_related_cash_source',
    'matching_contribution_related_cash_source_input',
    'reason',
    'reference_year',
  ] as const);

  const isSimplifiedFlow = isExemptFromCsgCrdsCampaign(type);
  // the consultation period edit of a published CET campaign must resend its persisted values
  const shouldApplySimplifiedFlowDefaults = isSimplifiedFlow && !isCetConsultationPeriodEdit;
  const isNonStandardReason = G.isNotNullable(reason) && reason !== 'standard' && !isSimplifiedFlow;
  const currentYear = new Date().getFullYear();
  const referenceYearChoices = Array.from({ length: 11 }, (_, index) => {
    const year = currentYear - index;
    return { id: year, name: String(year) };
  });
  const { value: passValueForReferenceYear, isLoaded: isPassForReferenceYearLoaded } =
    useHistoricalFinancialIndexValue(
      FINANCIAL_INDEX_SHORT_NAME.PASS,
      isNonStandardReason && G.isNotNullable(referenceYear) ? `${referenceYear}-01-01` : null,
    );
  const isPassMissingForReferenceYear =
    isNonStandardReason &&
    G.isNotNullable(referenceYear) &&
    isPassForReferenceYearLoaded &&
    G.isNullable(passValueForReferenceYear);

  const { data: contracts } = useGetList<GetB2bContractsResponse[number]>(
    `${resourceBasePath}contracts`,
    {
      sort: { field: 'created_at', order: 'DESC' },
      filter: { id: contractIds },
    },
    { enabled: Array.isArray(contractIds) && contractIds.length > 0 },
  );

  const firstContractIndex = getFirstContractIndex(
    contracts?.map((contract) => contract.contract_type) ?? null,
  );
  const firstContract =
    G.isNotNullable(firstContractIndex) && contracts ? contracts[firstContractIndex] : null;

  // The campaign computes a single distribution for all its contracts, so it cannot run when the
  // selected contracts do not share the same distribution rules on the campaign agreement.
  const hasDifferentAgreementDistributions =
    G.isNotNullable(contracts) &&
    allocationComputedBy === 'distributor' &&
    !areContractsAgreementDistributionsSimilar(type, contracts);

  const { data: plans } = useGetList<GetB2bCompaniesPlansResponse[number]>(
    `${resourceBasePath}companies/plans`,
    {
      sort: { field: 'created_at', order: 'DESC' },
      filter: {
        b2b_contract_id: contractIds,
      },
    },
    {
      enabled: Array.isArray(contractIds) && contractIds.length > 0,
    },
  );

  const { data: fetchedCompany } = useGetOne<Company>(
    `${resourceBasePath}companies`,
    {
      id: G.isNotNullable(companyId) ? companyId : 0,
    },
    { enabled: G.isNotNullable(companyId) && !isOnBoExternal },
  );

  const externalIdentity = isBoExternalIdentity(identity) ? identity : undefined;
  const company = isOnBoExternal
    ? externalIdentity?.companies.find((company) => company.id === externalIdentity.companyId[0])
    : fetchedCompany;

  // TODO: https://sh4res.atlassian.net/browse/BRAV-9199 start_fiscal_year is actually end_fiscal_year
  const [endFiscalYearDay, endFiscalYearMonth] =
    company?.start_fiscal_year.split('/').map(Number) ?? [];

  const isCompanyVoluntaryDepositsDisplayed =
    G.isNotNullable(firstContract) && esContractTypes.includes(firstContract.contract_type);
  const participationCampaign = firstContract?.campaigns.participation;
  const isMatchingContributionTypeDisplayed =
    (firstContract?.matching_contributions_enabled &&
      G.isNotNullable(mcComputedBy) &&
      (participationCampaign?.matching_contribution_computed_by === 'company' ||
        (participationCampaign?.matching_contribution_computed_by === 'distributor' &&
          !isOnBoExternal))) ??
    false;

  const isMatchingContributionType =
    type === 'matching_contribution' && Array.isArray(contractIds) && contractIds.length > 0;

  // Remembers the last non-null value so the switch below can restore it when re-enabled.
  const lastMatchingContributionComputedByRef = useRef<B2bCampaignActionHandler>('company');
  useEffect(() => {
    if (G.isNotNullable(mcComputedBy)) {
      lastMatchingContributionComputedByRef.current = mcComputedBy;
    }
  }, [mcComputedBy]);

  // The reset of the matching contribution below is skipped once the switch has been toggled, so
  // the switch must not be offered when no plan cash source of the type supports matching
  // contribution: turning it on would bring back a column that cannot be computed.
  const isTypeCashSourceWithoutMatchingContributionOnPlans =
    G.isNotNullable(type) &&
    type !== 'matching_contribution' &&
    G.isNotNullable(plans) &&
    !plans.some((plan) => hasPlanCashSourceWithMatchingContribution(plan, type));

  const isMatchingContributionEnabledSwitchDisplayed =
    !isMatchingContributionType &&
    !isTypeCashSourceWithoutMatchingContributionOnPlans &&
    (mcComputedBy === 'company' ||
      (G.isNullable(mcComputedBy) &&
        (isMatchingContributionManuallyOverridden || type === 'voluntary_deposit') &&
        lastMatchingContributionComputedByRef.current === 'company'));

  // Outside of matching contribution, the related campaign can still be linked when the campaign
  // corrects another one (adjustment / regularization / omission reasons).
  const isRelatedCampaignForCorrection =
    G.isNotNullable(type) &&
    type !== 'matching_contribution' &&
    G.isNotNullable(reason) &&
    (B2bRelatedCampaignCorrectionReasons as readonly string[]).includes(reason) &&
    Array.isArray(contractIds) &&
    contractIds.length > 0;

  const { data: companyCampaigns } = useGetList<GetB2bCampaignsResponse[number]>(
    `${resourceBasePath}campaigns`,
    {
      sort: { field: 'created_at', order: 'DESC' },
      filter: {
        b2b_contract_ids: contractIds,
        type: isRelatedCampaignForCorrection
          ? type
          : [
              'participation',
              'profit_sharing',
              'value_sharing_bonus',
              'unused_vacation_days',
              'voluntary_deposit',
            ],
      },
    },
    {
      enabled:
        G.isNotNullable(companyId) &&
        (isMatchingContributionType || isRelatedCampaignForCorrection),
    },
  );

  // For matching contribution campaigns, the selected fiscal year (derived from the reference
  // fiscal period start) determines which of the impacted plans actually have a matching
  // contribution config to run the campaign on.
  //
  // We only commit the year (and thus trigger a fetch) when the fiscal period start input loses
  // focus, so that typing a date character by character does not fire one request per keystroke.
  const [committedFiscalYear, setCommittedFiscalYear] = useState<number | null>(() => {
    const initialFiscalPeriodStart = getValues('fiscal_period_start');
    return G.isNotNullable(initialFiscalPeriodStart)
      ? new Date(initialFiscalPeriodStart).getFullYear()
      : null;
  });

  // allocation_computed_by can only be overridden from 'distributor' to 'company' because of the
  // distribution rules that must be filled on 'distributor'.
  const [derivedAllocationComputedBy, setDerivedAllocationComputedBy] =
    useState<B2bCampaignActionHandler | null>(null);
  const allocationComputedByReference =
    action === 'create' ? derivedAllocationComputedBy : (record?.allocation_computed_by ?? null);

  const commitFiscalYear = useCallback(() => {
    const fiscalPeriodStart = getValues('fiscal_period_start');
    setCommittedFiscalYear(
      G.isNotNullable(fiscalPeriodStart) ? new Date(fiscalPeriodStart).getFullYear() : null,
    );
  }, [getValues]);

  const impactedPlanIds = useMemo(
    () => plans?.flatMap((plan) => plan.shares_plans.map((subPlan) => subPlan.id)) ?? [],
    [plans],
  );

  const dataProvider = useDataProvider();

  const matchingContributionConfigYear =
    isNonStandardReason && G.isNotNullable(referenceYear) ? referenceYear : committedFiscalYear;

  const { data: plansMatchingContributionConfig } = useQuery(
    ['plansMatchingContributionConfigByYear', impactedPlanIds, matchingContributionConfigYear],
    async () => {
      const queryParams = new URLSearchParams({
        plan_ids: JSON.stringify(impactedPlanIds),
        year: String(matchingContributionConfigYear),
      });
      const { data } = await (
        dataProvider.getListWithQueryParams as (
          resource: string,
          queryParams: URLSearchParams,
        ) => Promise<{ data: GetPlansMatchingContributionConfigByYearResponse }>
      )(`${resourceBasePath}companies/plans/matching_contribution_config`, queryParams);
      return data;
    },
    {
      enabled:
        isMatchingContributionType &&
        G.isNotNullable(matchingContributionConfigYear) &&
        impactedPlanIds.length > 0,
    },
  );

  const planIdsWithMatchingContributionConfig = useMemo(
    () =>
      new Set(
        (plansMatchingContributionConfig ?? [])
          .filter((entry) => G.isNotNullable(entry.matching_contribution_config))
          .map((entry) => entry.plan_id),
      ),
    [plansMatchingContributionConfig],
  );

  // Once a fiscal year is selected, if none of the impacted plans have a matching contribution
  // config for that year the campaign cannot be configured at all.
  const hasNoMatchingContributionConfigForYear =
    isMatchingContributionType &&
    G.isNotNullable(matchingContributionConfigYear) &&
    G.isNotNullable(plansMatchingContributionConfig) &&
    impactedPlanIds.length > 0 &&
    planIdsWithMatchingContributionConfig.size === 0;

  // workaround to fix the permissions for now
  const typeChoices = getTypeChoices({
    isCompanyVoluntaryDepositsDisplayed,
    isMatchingContributionTypeDisplayed,
    contractCashSources,
    identity,
  });

  const matchingContributionRelatedCashSourceChoices =
    getMatchingContributionRelatedCashSourceChoices(firstContract, plans);

  // A matching contribution on company voluntary deposits abounds a given campaign, so the related
  // campaign is mandatory. On employee voluntary deposits there is no campaign to relate to.
  const isCompanyVoluntaryDepositCashSource =
    isMatchingContributionType &&
    isCompanyVoluntaryDepositCashSourceInput(matchingContributionRelatedCashSourceInput);
  const isEmployeeVoluntaryDepositCashSource =
    isMatchingContributionType &&
    isEmployeeVoluntaryDepositCashSourceInput(matchingContributionRelatedCashSourceInput);

  // Both voluntary deposit choices are saved as the `voluntary_deposit` cash source, so keep the
  // persisted field in sync with the form-only choice.
  useEffect(() => {
    if (!isMatchingContributionType || matchingContributionRelatedCashSourceInput === undefined) {
      return;
    }
    setValue(
      'matching_contribution_related_cash_source',
      toMatchingContributionRelatedCashSource(matchingContributionRelatedCashSourceInput),
      { shouldValidate: true },
    );
  }, [isMatchingContributionType, matchingContributionRelatedCashSourceInput, setValue]);

  useEffect(() => {
    if (!firstContract || !plans) {
      return;
    }

    if (shouldApplySimplifiedFlowDefaults) {
      setValue('reason', 'standard', { shouldValidate: true });
      setValue('amount_type_input', 'net', { shouldValidate: true });
      setValue('fiscal_period_start', null, { shouldValidate: true });
      setValue('fiscal_period_end', null, { shouldValidate: true });
    }

    const contractCashSources = mutualizeCashSources(plans.map((plan) => plan.cash_sources));
    setValue('contract_cash_sources', contractCashSources, { shouldValidate: true });

    if (!isMatchingContributionManuallyOverridden) {
      setValue(
        'matching_contribution_computed_by',
        firstContract.campaigns.participation?.matching_contribution_computed_by,
        {
          shouldValidate: true,
        },
      );
    }

    if (!type) {
      return;
    }

    if (!isMatchingContributionType && !A.includes(contractCashSources, type)) {
      setValue('type', null);
    }

    const {
      allocation_computed_by,
      choice_collected_by,
      payout_by,
      matching_contribution_computed_by,
    } = getCampaignProcessingOptionsFromType({
      type,
      contract: firstContract,
      isMatchingContributionComputedByNullOnAllPlans: plans.every((plan) =>
        G.isNullable(plan.matching_contribution_computed_by),
      ),
      mcEnabled: contracts?.some((contract) => contract.matching_contributions_enabled) ?? false,
    });

    setDerivedAllocationComputedBy(allocation_computed_by);

    // For matching contribution campaigns, once a fiscal year is selected we only keep a
    // computed-by handler for the plans that actually have a matching contribution config for
    // that year; the others are set to null.
    const shouldCheckMatchingContributionConfigByYear =
      isMatchingContributionType && G.isNotNullable(committedFiscalYear);

    const matchingContributionComputedByByPlanTypeLabeled: Record<
      string,
      B2bCampaignActionHandler | null
    > = {};
    plans.forEach((plan) => {
      // Same rule as the campaign-level value below: a plan whose cash source for the campaign
      // type has no matching contribution cannot have it computed by the company.
      const isPlanCashSourceWithoutMatchingContribution =
        type !== 'matching_contribution' &&
        plan.matching_contribution_computed_by === 'company' &&
        !hasPlanCashSourceWithMatchingContribution(plan, type);

      plan.shares_plans.forEach((subPlan) => {
        const planTypeLabeled = generatePlanTypeLabeled({
          planType: subPlan.type,
          planLabel: subPlan.plan_label,
        });
        matchingContributionComputedByByPlanTypeLabeled[planTypeLabeled] =
          isPlanCashSourceWithoutMatchingContribution ||
          (shouldCheckMatchingContributionConfigByYear &&
            !planIdsWithMatchingContributionConfig.has(subPlan.id))
            ? null
            : plan.matching_contribution_computed_by;
      });
    });

    // BO Internal agents may manually edit the processing options; once they have touched one of
    // these fields, stop overwriting their input on re-render (e.g. when navigating back to this
    // step) while still re-deriving them from a new contract or type until then. On edit,
    // type/contracts are locked so there is nothing to re-derive: the campaign's own persisted
    // values must simply stand.
    const isCreate = action === 'create';
    const shouldRederiveProcessingOptions =
      isCreate && shouldRederiveFromContract({ isOnBoExternal, isProcessingOptionsDirty });

    if (
      shouldRederiveProcessingOptions ||
      shouldLockAllocationToCompany(isCreate, allocation_computed_by)
    ) {
      setValue('allocation_computed_by', allocation_computed_by, { shouldValidate: true });
    }

    if (shouldRederiveProcessingOptions) {
      setValue('choice_collected_by', choice_collected_by, { shouldValidate: true });
      setValue('payout_by', payout_by, { shouldValidate: true });
    }
    // When the campaign type's cash source has no matching contribution on any of the plans that
    // carry it, the matching contribution cannot be computed by the company: reset it so the next
    // step does not expect (and generate) a matching contribution file.
    const isTypeCashSourceWithoutMatchingContribution =
      type !== 'matching_contribution' &&
      matching_contribution_computed_by === 'company' &&
      !plans.some((plan) => hasPlanCashSourceWithMatchingContribution(plan, type));

    if (!isMatchingContributionManuallyOverridden) {
      setValue(
        'matching_contribution_computed_by',
        isTypeCashSourceWithoutMatchingContribution ? null : matching_contribution_computed_by,
        { shouldValidate: true },
      );
    }

    setValue(
      'matching_contribution_computed_by_by_plan_type_labeled',
      matchingContributionComputedByByPlanTypeLabeled,
      {
        shouldValidate: true,
      },
    );

    if (type === 'value_sharing_bonus') {
      setValue('amount_type_input', 'net', { shouldValidate: true });
    }
  }, [
    setValue,
    firstContract,
    type,
    isMatchingContributionType,
    plans,
    contracts,
    shouldApplySimplifiedFlowDefaults,
    reason,
    committedFiscalYear,
    planIdsWithMatchingContributionConfig,
    isOnBoExternal,
    action,
    isProcessingOptionsDirty,
    setDerivedAllocationComputedBy,
    isMatchingContributionManuallyOverridden,
  ]);

  // Expose the "no matching contribution config for the selected year" state to the form so the
  // stepper can disable the next and save buttons on this step.
  useEffect(() => {
    setValue(
      'matching_contribution_config_missing_for_year',
      hasNoMatchingContributionConfigForYear,
    );
  }, [hasNoMatchingContributionConfigForYear, setValue]);

  useEffect(() => {
    setValue('pass_missing_for_reference_year', isPassMissingForReferenceYear);
  }, [isPassMissingForReferenceYear, setValue]);

  // Same for the mismatching distribution rules between the selected contracts.
  useEffect(() => {
    setValue('contracts_agreement_distributions_mismatch', hasDifferentAgreementDistributions);
  }, [hasDifferentAgreementDistributions, setValue]);

  useValueChanged(companyId, () => {
    setValue('b2b_contract_ids', []);
    setValue('allocations', null);
    setValue('allocations_file', null);
  });

  useValueChanged(type, () => {
    setValue('matching_contribution_related_cash_source', null);
    setValue('matching_contribution_related_cash_source_input', null);
    setValue('related_campaign_id', null);
    setValue('allocations', null);
  });

  // Watching the form-only choice, so that switching between employee and company voluntary
  // deposits (which share the same persisted cash source) also drops the related campaign.
  useValueChanged(matchingContributionRelatedCashSourceInput, () => {
    setValue('related_campaign_id', null);
    setValue('allocations', null);
  });

  useValueChanged(reason, () => {
    if (!isRelatedCampaignForCorrection) {
      setValue('related_campaign_id', null);
    }
    if (reason === 'standard') {
      setValue('reference_year', null, { shouldValidate: true });
    }
  });

  useEffect(() => {
    if (allocationComputedBy === 'distributor') {
      setValue('amount_type_input', 'gross');
    }
  }, [allocationComputedBy, setValue]);

  useValueChanged(choiceCollectedBy, () => {
    // Company-collected allocations carry the investment choices made offline, distributor-collected
    // ones only carry an amount, so a file imported under the previous option cannot be reused. The
    // backend drops the stale choices the same way when the switch happens on an existing campaign.
    setValue('allocations', null);
    setValue('allocations_file', null);

    // Only a distributor-collected campaign runs a consultation period, so handing the collection
    // back to the company drops the dates entered for the previous option. They are kept in the
    // other direction: this step remounts on every back-and-forth through the stepper, and
    // clearing them here would silently undo what was entered on the allocations step.
    if (action === 'create' && choiceCollectedBy === 'company') {
      setValue('start_date', undefined, { shouldValidate: true });
      setValue('end_date', undefined, { shouldValidate: true });
    }
  });

  useEffect(() => {
    if (type === 'value_sharing_bonus' && choiceCollectedBy === 'company') {
      setValue('payout_by', 'company');
    }
  }, [choiceCollectedBy, type, setValue]);

  useEffect(() => {
    if (type === 'value_sharing_bonus') {
      if (!valueSharingBonusFiscalRule) {
        setValue('value_sharing_bonus_fiscal_rule', 'without_csg_crds_deduction', {
          shouldValidate: true,
        });
      }
    } else if (valueSharingBonusFiscalRule) {
      setValue('value_sharing_bonus_fiscal_rule', null, { shouldValidate: true });
    }
  }, [type, valueSharingBonusFiscalRule, setValue]);

  useValueChanged(amountTypeInput, () => {
    setValue('allocations', null);
    setValue('allocations_file', null);
  });

  return (
    <Box>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.campaigns.sections.general.details"
        noCard={true}>
        {isOnBoExternal ? (
          <SelectInput
            data-testid={`${testId}_CompanySelect`}
            source="company_id"
            validate={required()}
            isRequired
            defaultValue={company?.id}
            choices={
              G.isNotNullable(company)
                ? [{ id: company.id, name: `(${company.id}) ${company.name}` }]
                : []
            }
            {...inputDisabledProps(true)}
          />
        ) : (
          <B2bCompanySelectInput
            source="company_id"
            validate={required()}
            reference={`${resourceBasePath}companies`}
            isRequired
            testId={`${testId}_CompanyReferenceSelect`}
            {...inputDisabledProps(action === 'edit' || editionDisabled)}
          />
        )}
        <B2bContractArraySelectInput
          source="b2b_contract_ids"
          reference={`${resourceBasePath}contracts`}
          filter={G.isNotNullable(companyId) ? { b2b_company_id: companyId } : {}}
          validate={required()}
          campaignType={type}
          isRequired
          testId={`${testId}_ContractSelect`}
          {...inputDisabledProps(G.isNullable(companyId) || action === 'edit' || editionDisabled)}
          {...(G.isNullable(companyId) && {
            helperText: translate(
              'resources.bo_common.b2b.campaigns.sections.general.select_company_first',
            ),
          })}
        />
        {plans && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              padding: '5px 10px 15px 10px',
              gap: '20px',
              alignItems: 'center',
            }}>
            <Box>{translate('resources.bo_common.b2b.campaigns.fields.products')}</Box>
            <ProductsField
              data-testid={`${testId}_ProductsField`}
              record={{
                id: companyId ?? 0,
                products: plans.map((plan) => plan.category),
                plan_labels: plans.map((plan) => plan.plan_label),
              }}
              plansHaveCampaignTypeAsCashSource={
                G.isNullable(type) ||
                (type === 'matching_contribution' &&
                  G.isNullable(matchingContributionRelatedCashSource))
                  ? undefined
                  : plans.reduce(
                      (result: Record<string, boolean>, plan) => {
                        const typeToUse =
                          type === 'matching_contribution'
                            ? matchingContributionRelatedCashSource
                            : type;
                        result[
                          `${plan.category}${plan.plan_label ? ` (${plan.plan_label})` : ''}`
                        ] = A.includes(plan.cash_sources, typeToUse);
                        return result;
                      },
                      {} as Record<string, boolean>,
                    )
              }
              hasNoCommonCashSources={contractCashSources?.length === 0}
              hasDifferentAgreementDistributions={hasDifferentAgreementDistributions}
              source="products"
            />
          </Box>
        )}
        <SelectInput
          data-testid={`${testId}_TypeSelect`}
          choices={typeChoices}
          source="type"
          validate={required()}
          isRequired
          {...inputDisabledProps(action === 'edit' || editionDisabled)}
        />
        {type === 'value_sharing_bonus' && (
          <SelectInput
            data-testid={`${testId}_FiscalRuleSelect`}
            choices={valueSharingBonusFiscalRuleChoices}
            source="value_sharing_bonus_fiscal_rule"
            validate={[required()]}
            isRequired
            helperText="resources.bo_common.b2b.campaigns.fields.value_sharing_bonus_fiscal_rule_helper"
            {...inputDisabledProps(editionDisabled)}
          />
        )}
        {!isSimplifiedFlow && (
          <SelectInput
            data-testid={`${testId}_ReasonSelect`}
            choices={b2bCampaignReasonChoices.filter((choice) => {
              if (isOnBoExternal && ['adjustment', 'regularization'].includes(choice.id)) {
                return false;
              }
              return true;
            })}
            source="reason"
            validate={[required()]}
            isRequired
            {...inputDisabledProps(
              editionDisabled && !(isOngoingConsultationEdit && isStartDateInFuture),
            )}
          />
        )}
        {isNonStandardReason && (
          <>
            <SelectInput
              data-testid={`${testId}_ReferenceYearSelect`}
              choices={referenceYearChoices}
              source="reference_year"
              validate={[required()]}
              isRequired
              {...inputDisabledProps(
                editionDisabled && !(isOngoingConsultationEdit && isStartDateInFuture),
              )}
            />
            {G.isNotNullable(referenceYear) && G.isNotNullable(passValueForReferenceYear) && (
              <Typography>
                {translate('resources.bo_common.b2b.campaigns.fields.pass_for_reference_year', {
                  year: referenceYear,
                  amount: formatCentsNumber(passValueForReferenceYear),
                })}
              </Typography>
            )}
            {isPassMissingForReferenceYear && (
              <Alert severity="error">
                {translate('resources.bo_common.b2b.campaigns.fields.pass_not_found_for_year', {
                  year: referenceYear,
                })}
              </Alert>
            )}
          </>
        )}
        {isMatchingContributionType && (
          <SelectInput
            data-testid={`${testId}_MatchingCashSourceSelect`}
            choices={matchingContributionRelatedCashSourceChoices}
            source="matching_contribution_related_cash_source_input"
            defaultValue={toMatchingContributionRelatedCashSourceInput(record)}
            validate={[required()]}
            isRequired
            {...inputDisabledProps(editionDisabled)}
          />
        )}
        {((isMatchingContributionType &&
          G.isNotNullable(matchingContributionRelatedCashSourceInput) &&
          !isEmployeeVoluntaryDepositCashSource) ||
          isRelatedCampaignForCorrection) && (
          <SelectInput
            choices={
              companyCampaigns
                ?.filter((campaign) =>
                  isMatchingContributionType
                    ? campaign.type === matchingContributionRelatedCashSource
                    : campaign.type === type,
                )
                .map((campaign) => ({
                  id: campaign.id,
                  name: `#${campaign.id} ${translate(`resources.bo_common.b2b.campaigns.choices.type.${campaign.type}`)}
                    ${
                      G.isNotNullable(campaign.fiscal_period_start) &&
                      G.isNotNullable(campaign.fiscal_period_end)
                        ? ` : ${tryToLocalDate(campaign.fiscal_period_start)?.toLocaleDateString()} - ${tryToLocalDate(campaign.fiscal_period_end)?.toLocaleDateString()}`
                        : ''
                    }`,
                })) ?? []
            }
            data-testid={`${testId}_MatchingCampaignSelect`}
            source="related_campaign_id"
            {...(isCompanyVoluntaryDepositCashSource && {
              validate: [required()],
              isRequired: true,
            })}
            {...inputDisabledProps(editionDisabled)}
          />
        )}
        {isMatchingContributionEnabledSwitchDisplayed && (
          <>
            {G.isNotNullable(mcComputedBy) && (
              <SelectInput
                data-testid={`${testId}_MatchingComputedBySelect`}
                choices={contractCampaignActionHandlerChoices}
                source="matching_contribution_computed_by"
                {...inputDisabledProps(true)}
              />
            )}
            <FormControlLabel
              label={translate(
                'resources.bo_common.b2b.campaigns.fields.matching_contribution_enabled',
              )}
              control={
                <Switch
                  data-testid={`${testId}_MatchingContributionEnabledSwitch`}
                  checked={G.isNotNullable(mcComputedBy)}
                  disabled={isOnBoExternal || isCampaignAlreadyStarted}
                  onChange={(_event, checked) => {
                    setIsMatchingContributionManuallyOverridden(true);
                    setValue(
                      'matching_contribution_computed_by',
                      checked ? lastMatchingContributionComputedByRef.current : null,
                      { shouldValidate: true, shouldDirty: true },
                    );
                  }}
                />
              }
            />
          </>
        )}
      </ShowSection>

      {!isSimplifiedFlow && (
        <>
          <ShowSection
            noCard
            leftAligned
            label="resources.bo_common.b2b.campaigns.sections.general.operation_processing">
            <SelectInput
              data-testid={`${testId}_ChoiceCollectedBySelect`}
              choices={contractAllocationChoicesFor(allocationComputedBy)}
              source="choice_collected_by"
              isRequired
              {...inputDisabledProps(isOnBoExternal || isCampaignAlreadyStarted)}
            />
            {!isMatchingContributionType && (
              <SelectInput
                data-testid={`${testId}_PayoutBySelect`}
                choices={contractCampaignActionHandlerChoices}
                source="payout_by"
                isRequired
                {...inputDisabledProps(
                  isOnBoExternal ||
                    isCampaignAlreadyStarted ||
                    (type === 'value_sharing_bonus' && choiceCollectedBy === 'company'),
                )}
              />
            )}
            <SelectInput
              data-testid={`${testId}_AllocationComputedBySelect`}
              choices={contractCampaignActionHandlerChoices}
              source="allocation_computed_by"
              isRequired
              {...inputDisabledProps(
                isOnBoExternal ||
                  isCampaignAlreadyStarted ||
                  type === 'unused_vacation_days' ||
                  type === 'value_sharing_bonus' ||
                  allocationComputedByReference === 'company',
              )}
            />
          </ShowSection>
          <OperationAmountSection
            isEditionDisabled={editionDisabled && !isOngoingConsultationEdit}
            isAmountTypeEditionDisabled={isOngoingConsultationEdit}
            isAgreementCommonForGroup={contracts?.some(
              (contract) =>
                (type === 'participation' &&
                  contract.participation?.is_agreement_common_for_group) ||
                (type === 'profit_sharing' &&
                  contract.profit_sharing?.is_agreement_common_for_group) ||
                (type === 'value_sharing_bonus' &&
                  contract.value_sharing?.is_agreement_common_for_group),
            )}
          />
          <ReferenceFiscalPeriod
            isEditionDisabled={
              (editionDisabled && !isOngoingConsultationEdit) ||
              // while the campaign is ongoing, the reference fiscal period can no longer be edited once late interests apply
              (isOngoingConsultationEdit && !!record?.is_late_payment_interests)
            }
            endFiscalYearDay={endFiscalYearDay}
            endFiscalYearMonth={endFiscalYearMonth}
            onFiscalPeriodStartCommit={commitFiscalYear}
          />
          {hasNoMatchingContributionConfigForYear && (
            <Alert severity="warning">
              {translate(
                'resources.bo_common.b2b.campaigns.sections.general.no_matching_contribution_config_for_year',
              )}
            </Alert>
          )}
        </>
      )}
      {isCampaignVacationDaysAllocation({
        type,
        allocation_computed_by: allocationComputedBy,
        choice_collected_by: choiceCollectedBy,
      }) && (
        <VacationDaysSetupSection
          isEditionDisabled={editionDisabled}
          isConsultationPeriodEditionDisabled={editionDisabled && !isCetConsultationPeriodEdit}
          isStartDateEditionDisabled={
            editionDisabled && !(isCetConsultationPeriodEdit && isStartDateInFuture)
          }
        />
      )}
      {!isOnBoExternal && (
        <BooleanInput
          source="is_emails_enabled"
          defaultValue={true}
          data-testid={`${testId}_IsEmailsEnabledInput`}
        />
      )}
    </Box>
  );
};
